import { Bundle, BundleEntry, CareTeam, Device, FhirResource, Organization, Patient, Practitioner, RelatedPerson } from "fhir/r4";
import i18n from "i18next";
import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Form, InputGroup } from "react-bootstrap";
import AcesoPage from "src/components/AcesoPage/AcesoPage";
import { Title } from "@fyrstain/fhir-front-library";
import DataTable, { DataTableColumn } from "src/components/DataTable/DataTable";
import { useNavigate } from "react-router";
import Client from "fhir-kit-client";
import { useTranslation } from "react-i18next";

interface CareTeamRow {
    id: string;
    subject: string;
    participants: string[];
}

const CareteamPage: FunctionComponent = () => {
    const { t } = useTranslation();

    /////////////////////////////////////
    //        Search Parameters        //
    /////////////////////////////////////

    const [patientName, setPatientName] = useState("");
    const [patientSurname, setPatientSurname] = useState("");
    const [patientId, setPatientId] = useState("");

    /////////////////////////////////////
    //              Table              //
    /////////////////////////////////////

    const columns: DataTableColumn<CareTeamRow>[] = [
        {
            text: t("forms.careTeam.subject"),
            dataField: "subject"
        },
        {
            text: t("forms.careTeam.participants"),
            dataField: "participants",
            formatter: (cell: string[]) => {
                return <div>
                    {cell.map(c => {
                        return <><span>{c}</span><br /></>;
                    })}
                </div>;
            },
        }];
    const [data, setData] = useState<CareTeamRow[]>([]);

    /////////////////////////////////////
    //             Client              //
    /////////////////////////////////////

    const fhirClient = new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
    });

    /////////////////////////////////////
    //             Actions             //
    /////////////////////////////////////

    const navigate = useNavigate();

    /**
     * Search DocumentReference from the back to populate the table.
     * 
     * @returns     the Promise of the List of DocumentReference.
     */
    async function searchTestPlans(): Promise<CareTeamRow[]> {
        return searchTestPlansWithParams(patientName, patientSurname, patientId);
    }

    /**
     * Search DocumentReference from the back to populate the table.
     * 
     * @returns     the Promise of the List of DocumentReference.
     */
    async function searchTestPlansWithParams(patientName: string | null, patientSurname: string | null, patientId: string | null): Promise<CareTeamRow[]> {
        try {
            const response = await fhirClient.search({
                resourceType: 'CareTeam',
                searchParams: buildSearchParameters(patientName, patientSurname, patientId)
            },
            );
            if (response.resourceType !== 'Bundle') {
                throw Error(response.statusText);
            }
            const bundle: Bundle = response as Bundle;
            return bundle.entry?.filter(
                entry => 'CareTeam' === entry.resource?.resourceType
            ).map(
                entry => entry.resource as CareTeam
            ).map(
                careTeam => {
                    return {
                        "id": careTeam.id ?? "",
                        "subject": getSubject(careTeam, bundle.entry ?? []),
                        "participants": getParticipants(careTeam, bundle.entry ?? []),
                    }
                }
            ) ?? [];
        } catch (error) {
            console.log(error);
            onError();
        }
        return [];
    }

    function getSubject(careTeam: CareTeam, entries: BundleEntry<FhirResource>[]) {
        const subjectId = careTeam.subject?.reference?.split('/')[1];
        const subjectResource = entries.find(entry => subjectId === entry.resource?.id)?.resource;
        if (subjectResource && 'Patient' === subjectResource.resourceType) {
            const subjectName = (subjectResource as Patient).name?.find(name => name);
            return subjectName?.given?.join(", ") + " " + subjectName?.family;
        }
        return t("general.notavailable");
    }

    function getParticipants(careTeam: CareTeam, entries: BundleEntry<FhirResource>[]) {
        const result = [] as string[];
        careTeam.participant?.forEach(participant => {
            const participantId = participant.member?.reference?.split('/')[1];

            const participantResource = entries.find(entry => participantId === entry.resource?.id)?.resource;
            if (participantResource) {
                switch (participantResource.resourceType) {
                    //TODO PractitionerRole
                    case 'Practitioner':
                        result.push(getPractitionerLabel(participantResource));
                        break;
                    case 'Organization':
                        result.push(getOrganizationLabel(participantResource));
                        break;
                    case 'RelatedPerson':
                        result.push(getRelatedPersonLabel(participantResource));
                        break;
                    case 'Device':
                        result.push(getDeviceLabel(participantResource));
                        break;
                }
            }
        })
        return result;
    }

    function getPractitionerLabel(practitioner: Practitioner) {
        const practitionerName = practitioner.name?.find(name => name);
        return "(P) " + practitionerName?.given?.join(", ") + " " + practitionerName?.family;
    }

    function getOrganizationLabel(organization: Organization) {
        return "(O) " + organization.name;
    }

    function getRelatedPersonLabel(relatedPerson: RelatedPerson) {
        const relatedPersonName = relatedPerson.name?.find(name => name);
        return "(RP) " + relatedPersonName?.given?.join(", ") + " " + relatedPersonName?.family;
    }

    function getDeviceLabel(device: Device) {
        const deviceName = device.deviceName?.find(name => 'user-friendly-name' === name.type);
        return "(D) " + deviceName?.name;
    }

    /**
     * Build the SearchParameters for the Search used to populate the table.
     * 
     * @param patientName    the name of the subject.
     * @param patientSurname    the given name of the subject.
     * @param patientId    the technicalId of the patient.
     * @returns     the SearchParameters as a JSON object.
     */
    function buildSearchParameters(patientName: string | null | undefined, patientSurname: string | null | undefined, patientId: string | null | undefined): {} {
        var searchParams: {} = { "_sort": "-date", "_include": "CareTeam:*" };

        if (patientName && patientName.length > 0) {
            searchParams = {
                "patient:Patient.family": patientName,
                ...searchParams
            }
        }
        if (patientSurname && patientSurname.length > 0) {
            searchParams = {
                "patient:Patient.given": patientSurname,
                ...searchParams
            }
        }
        if (patientId && patientId.length > 0) {
            searchParams = {
                "subject": "Patient/" + patientId,
                ...searchParams
            }
        }
        return searchParams;
    }

    /**
     * Update the state with the updated Search result when the Search button is pressed.
     */
    async function onSearch() {
        setData(await searchTestPlans());
    }

    /**
     * Reset the values of the Search Criteria fields and the Search Results.
     */
    async function onReset() {
        setPatientSurname("");
        setPatientId("");
        setPatientName("");
        setData(await searchTestPlansWithParams(null, null, null));
    }

    const onError = useCallback((error?: unknown) => {
        navigate("/Error", { state: { error } });
    }, [navigate]);

    const initialSearchRef = useRef(onSearch);
    initialSearchRef.current = onSearch;

    useEffect(() => {
        initialSearchRef.current();
    }, []);


    return <AcesoPage loading={false} titleKey='title.carecircle' needsLogin={true}>
        <>
            <div className="section">
                <Card>
                    <Card.Header>
                        <Title level={2} content={i18n.t('title.searchcriteria')} />
                    </Card.Header>
                    <Card.Body className="cardBody">
                        <div className="mediumContainer">
                            <Form.Label>{t("forms.careTeam.patientLastName")} :</Form.Label>
                            <InputGroup className="mb-3">
                                <Form.Control
                                    placeholder=""
                                    aria-label="patientName"
                                    aria-describedby="patientName"
                                    value={patientName}
                                    onChange={(event) => setPatientName(event.target.value)}
                                />
                            </InputGroup>
                        </div>
                        <div className="spaceBetweenContainer">
                            <div className="mediumContainer">
                                <Form.Label>{t("forms.careTeam.patientFirstName")} : </Form.Label>
                                <InputGroup className="mb-3">
                                    <Form.Control
                                        placeholder=""
                                        aria-label="patientSurname"
                                        aria-describedby="patientSurname"
                                        value={patientSurname}
                                        onChange={(event) => setPatientSurname(event.target.value)}
                                    />
                                </InputGroup>
                            </div>
                            <div className="mediumContainer">
                                <Form.Label>{t("forms.careTeam.patientId")} : </Form.Label>
                                <InputGroup className="mb-3">
                                    <Form.Control
                                        placeholder=""
                                        aria-label="patientId"
                                        aria-describedby="patientId"
                                        value={patientId}
                                        onChange={(event) => setPatientId(event.target.value)}
                                    />
                                </InputGroup>
                            </div>
                        </div>
                        <div className="formActions">
                            <Button
                                className="button"
                                variant="danger"
                                onClick={onSearch}
                            >
                                {i18n.t('button.search')}
                            </Button>
                            <Button
                                className="button"
                                variant="secondary"
                                onClick={onReset}
                            >
                                {i18n.t('button.reset')}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </div>
            <div className="section">
                <DataTable
                    keyField="id"
                    data={data}
                    columns={columns}
                />
            </div>
        </>
    </AcesoPage>
};

export default CareteamPage;
